/**
 * Auth / apply-now English copy (Angular parity wording).
 * UI components must receive these strings as props — do not hardcode copy in JSX.
 * Spanish (when enabled) is handled at runtime by Google Translate.
 */
export function getAuthCopyright(year = new Date().getFullYear()): string {
  return `© 2020-${year} Liberty Rent. All Rights Reserved.`
}

const DEFAULT_LOGIN_LOGO = '/LibertyRent_Logo_Horizontal_SCLA_PMS2217.png'

export const AUTH_CONTENT = {
  common: {
    copyright: getAuthCopyright(),
    selectLanguage: 'Select Language',
    accessibility: 'Accessibility',
    ok: 'Ok',
    signIn: 'Sign In',
  },
  sandboxWarning: {
    message:
      'This is a Sandbox platform. If you received an email redirecting to this URL Please visit',
    cornerstoneLink: 'https://cornerstone.libertyrent.com',
    cornerstoneUrl: 'https://cornerstone.libertyrent.com',
    helpEmail: 'help@libertyrent.com',
    helpEmailAddress: 'help@libertyrent.com',
  },
  login: {
    pageTitle: 'Login - Liberty Rent',
    defaultLogoSrc: DEFAULT_LOGIN_LOGO,
    logo: {
      title: 'Liberty Rent',
      subtitle: 'SECOND CHANCE LEASE APPROVAL',
    },
    form: {
      welcomeBack: 'Welcome Back',
      welcomeSubtitle: 'Sign in to continue to Liberty Rent',
      emailLabel: 'Email address',
      emailPlaceholder: 'Enter your email',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Enter your password',
      signInButton: 'SIGN IN',
      signingInButton: 'SIGNING IN...',
      forgotPassword: 'Forgot Password?',
      newToLibertyRent: 'New to Liberty Rent?',
      applyNow: 'APPLY NOW',
    },
    validation: {
      emailRequired: 'Email is required',
      emailInvalid: 'Please enter a valid email address',
      passwordRequired: 'Password is required',
      passwordMinLength: 'Password must be at least 6 characters',
    },
    accessDenied: 'Access Denied',
    unregisteredEmail:
      'The email address entered is not registered in our system.',
  },
  forgotPassword: {
    accountAccess: 'Account access',
    title: 'Forgot Password',
    instructions:
      'Please Allow **5 minutes** to receive your new password. Check your **Spam Folder** for the email.',
    emailLabel: 'Email',
    emailPlaceholder: 'Enter your email',
    helperText: '',
    sendButton: 'Send',
    sendingButton: 'Sending...',
    backToLogin: 'Back to Login',
    rememberedPassword: 'Remembered your password?',
    signIn: 'Sign In',
    sendFailed: 'Failed to send reset email. Please try again.',
  },
  applyNow: {
    applicationSetup: 'Application setup',
    continue: 'Continue',
    goBack: 'Go Back',
    alreadyHaveAccount: 'Already have a User Account?',
    signIn: 'Sign In',
    step1: {
      title: 'Have you applied at the property?',
      subtitle: '',
      noOption: 'No',
      noDescription: "I haven't yet",
      yesOption: 'Yes',
      yesDescription: 'And I was referred to Liberty Rent',
    },
    step2: {
      title: 'Do you have an invite code?',
      description:
        "Some communities require an invite code to use our program. If you did not receive one they probably don't require it. Later in the registration process if our system recognizes that your property requires it you will need to request one from your property.",
      noOption: 'No',
      noDescription: "I didn't get one",
      yesOption: 'Yes',
      yesDescription: 'I have an invite code',
      yesUsedOption: 'Yes',
      yesUsedDescription:
        'I have an invite code and have already used it. Please Sign In with your Email and Password.',
    },
    step3: {
      title: 'Enter your invite code below',
      description:
        'We will use your invite code to lookup all of your information making the registration process quicker and easier for you.',
      placeholder: 'Enter invite code here',
      nextButton: 'Next',
      verifying: 'Verifying...',
      validation: {
        codeRequired: 'Please enter an invite code',
        codeInvalid: 'Invalid invite code. Please try again.',
        verifyFailed: 'Failed to verify code. Please try again.',
      },
      alreadyUsed: {
        title: 'Invite code already used',
        paragraph1:
          'The Invitation Code has already been used. Please Sign-in with your registered email and password.',
        paragraph2:
          'If you have not used your Invitation yet, then contact your Leasing Consultant to issue a new invitation code.',
        close: 'CLOSE',
        signIn: 'SIGN IN',
      },
    },
    step4: {
      title: 'You need to apply with one of our partner properties first.',
      description:
        "Start by applying with an apartment community. They will verify if they have units available and will determine if you need our help. If you would like to search properties we've partnered with, contact customer service at {phone} or {email}",
      phone: '(877) 581-7368',
      email: 'help@libertyrent.com',
    },
  },
} as const

export type AuthContent = typeof AUTH_CONTENT

export type ApplyNowShellLabels = {
  applicationSetup: string
  continue: string
  goBack: string
  alreadyHaveAccount: string
  signIn: string
  copyright: string
}

export const APPLY_NOW_SHELL_LABELS: ApplyNowShellLabels = {
  applicationSetup: AUTH_CONTENT.applyNow.applicationSetup,
  continue: AUTH_CONTENT.applyNow.continue,
  goBack: AUTH_CONTENT.applyNow.goBack,
  alreadyHaveAccount: AUTH_CONTENT.applyNow.alreadyHaveAccount,
  signIn: AUTH_CONTENT.applyNow.signIn,
  copyright: getAuthCopyright(),
}
